import { motion } from "framer-motion";
import {
  GitCommit,
  Code2,
  Activity,
  ShieldCheck,
  MonitorSmartphone,
  Cpu,
} from "lucide-react";
import SectionLabel from "@/components/sections/SectionLabel";

const steps = [
  {
    n: "01",
    icon: GitCommit,
    title: "Watches Every Commit",
    body: "Continuously watches frontend and backend commits. No manual trigger required.",
    bullets: ["GitHub · GitLab · Bitbucket", "Main, feature & PR branches", "Zero CI maintenance"],
  },
  {
    n: "02",
    icon: Code2,
    title: "Understands Your Codebase",
    body: "Reads frontend and backend architecture — not just changed files, but impact.",
    bullets: ["Routes & components", "APIs & services", "Data flow & dependencies"],
  },
  {
    n: "03",
    icon: Activity,
    title: "Predicts Impact",
    body: "Instead of running every test, predicts what truly needs deep validation.",
    bullets: ["Affected screens", "Impacted user journeys", "APIs touched by the diff"],
  },
  {
    n: "04",
    icon: ShieldCheck,
    title: "Becomes Your Best QA Engineer",
    body: "Behaves exactly like an experienced manual QA — clicks naturally, tries unexpected flows, explores hidden paths.",
    bullets: ["Edge cases first", "Interrupts journeys", "Real user mindset"],
  },
  {
    n: "05",
    icon: MonitorSmartphone,
    title: "Tests Across Everything",
    body: "One run covers every viewport, network, mode and identity your customers use.",
    bullets: ["Desktop · tablet · mobile", "Slow & fast networks", "Light, dark, incognito, auth"],
  },
  {
    n: "06",
    icon: Cpu,
    title: "Learns Forever",
    body: "Every discovered issue makes future testing smarter. Institutional QA knowledge — encoded.",
    bullets: ["Historical bugs", "Fragile workflows", "Business-critical flows"],
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 border-t border-border"
    >
      {/* subtle grid bg */}
      <div className="absolute inset-0 bg-grid-sm radial-fade-top opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <SectionLabel>How it works</SectionLabel>
          <h2 className="font-display font-semibold mt-4 text-balance text-4xl sm:text-5xl md:text-[56px] leading-[1.05] tracking-[-0.025em]">
            From commit to confidence —{" "}
            <span className="text-muted-foreground">in six autonomous steps.</span>
          </h2>
        </div>

        <div
          data-testid="how-it-works-grid"
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`how-it-works-step-${i + 1}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="premium-card p-7 md:p-8 group relative overflow-hidden"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-[11px] tracking-widest text-muted-foreground">
                  STEP / {s.n}
                </span>
                <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent)/0.10)] border border-[hsl(var(--accent)/0.25)] text-[hsl(var(--accent))] inline-flex items-center justify-center group-hover:scale-[1.05] transition-transform">
                  <s.icon className="h-4.5 w-4.5" strokeWidth={1.7} />
                </div>
              </div>

              <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight">
                {s.title}
              </h3>
              <p className="mt-2.5 text-[14.5px] text-muted-foreground leading-relaxed">
                {s.body}
              </p>

              <ul className="mt-5 space-y-2">
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-center gap-2.5 text-[13px] text-foreground/75"
                  >
                    <span className="h-1 w-1 rounded-full bg-[hsl(var(--accent))]" />
                    {b}
                  </li>
                ))}
              </ul>

              {/* corner connector dot */}
              <div className="absolute right-5 bottom-5 h-1.5 w-1.5 rounded-full bg-foreground/10 group-hover:bg-[hsl(var(--accent))] transition-colors" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
